// File: src/themes/theme.js
import { createTheme, alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { getCyberTheme } from './themes/cyberTheme';
import { getPremiumTheme } from './themes/premiumTheme';

// ============================================================================
// ANIMATIONS - Exported for use throughout the app
// ============================================================================
export const appAnimations = {
  float: keyframes`
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(5deg); }
  `,
  pulse: keyframes`
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.05); }
  `,
  shimmer: keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  `,
  slideUp: keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  `,
  scaleIn: keyframes`
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  `,
  glowPulse: keyframes`
    0%, 100% { box-shadow: 0 0 20px rgba(255, 152, 0, 0.3); }
    50% { box-shadow: 0 0 40px rgba(255, 152, 0, 0.6); }
  `,
  betaPulse: keyframes`
    0%, 100% { box-shadow: 0 2px 8px rgba(255, 152, 0, 0.5); }
    50% { box-shadow: 0 2px 16px rgba(255, 152, 0, 0.8), 0 0 20px rgba(255, 87, 34, 0.4); }
  `,
  fadeIn: keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
  `,
};

// ============================================================================
// COLOR DEFINITIONS
// ============================================================================
const darkColors = {
  // Primary brand colors
  primary: {
    main: '#ff9800', 
    light: '#ffb74d',
    dark: '#f57c00',
    contrastText: '#000000',
  },
  secondary: {
    main: '#ff5722',
    light: '#ff7043',
    dark: '#e64a19',
    contrastText: '#ffffff',
  },
  
  // Background colors
  background: {
    default: '#0a0a0a',
    paper: '#1a1a1a',
    gradient: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0d0d0d 100%)',
    surface: 'rgba(255, 255, 255, 0.03)',
    surfaceHover: 'rgba(255, 255, 255, 0.05)',
    card: 'linear-gradient(145deg, rgba(30, 30, 30, 0.9), rgba(20, 20, 20, 0.95))',
  },
  
  // Text colors
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.7)',
    muted: 'rgba(255, 255, 255, 0.5)',
    disabled: 'rgba(255, 255, 255, 0.4)',
  },
  
  // Border colors
  border: {
    primary: 'rgba(255, 152, 0, 0.6)',
    light: 'rgba(255, 255, 255, 0.1)',
    hover: 'rgba(255, 152, 0, 0.4)',
    focus: '#ff9800',
  },
  
  // Decorative colors
  decorative: {
    orbPrimary: 'rgba(255, 152, 0, 0.08)',
    orbSecondary: 'rgba(255, 87, 34, 0.06)',
    shapePrimary: 'linear-gradient(135deg, rgba(255, 152, 0, 0.2), rgba(255, 87, 34, 0.1))',
    shapeRing: 'rgba(255, 152, 0, 0.3)',
    glow: '0 0 45px rgba(255, 152, 0, 0.3)',
  },
  
  // Status colors
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#388e3c',
    bg: 'rgba(46, 125, 50, 0.15)',
    border: 'rgba(46, 125, 50, 0.3)',
  },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
    bg: 'rgba(211, 47, 47, 0.15)',
    border: 'rgba(211, 47, 47, 0.3)',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
    bg: 'rgba(255, 152, 0, 0.15)',
    border: 'rgba(255, 152, 0, 0.3)',
  },
  info: {
    main: '#2196f3',
    light: '#64b5f6',
    dark: '#1976d2',
    bg: 'rgba(33, 150, 243, 0.15)',
    border: 'rgba(33, 150, 243, 0.3)',
  },

  // Logo
  logoAiText: { color: '#000000' },
  
  // Sidebar section colors
  sidebarSection: {
    workoutSessions: { main: '#FF9800', bg: alpha('#FF9800', 0.25) },
    myCalendars: { main: '#b39ddb', bg: alpha('#b39ddb', 0.25) },
    workoutPlans: { main: '#81c784', bg: alpha('#81c784', 0.25) },
    workoutLogs: { main: '#FF9800', bg: alpha('#FF9800', 0.25) },
    locations: { main: '#4db6ac', bg: alpha('#4db6ac', 0.25) },
    activities: { main: '#f06292', bg: alpha('#f06292', 0.25) },
    equipment: { main: '#64b5f6', bg: alpha('#64b5f6', 0.25) },
    statistics: { main: '#7986cb', bg: alpha('#7986cb', 0.25) },
  },
};

const lightColors = {
  primary: {
    main: '#f57c00',
    light: '#ff9800',
    dark: '#e65100',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#ff5722',
    light: '#ff7043',
    dark: '#e64a19',
    contrastText: '#ffffff',
  },
  
  background: {
    default: '#f5f5f5',
    paper: '#ffffff',
    gradient: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 50%, #fafafa 100%)',
    surface: 'rgba(0, 0, 0, 0.02)',
    surfaceHover: 'rgba(0, 0, 0, 0.04)',
    card: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(250, 250, 250, 0.9))',
  },
  
  text: {
    primary: '#212121',
    secondary: 'rgba(0, 0, 0, 0.7)',
    muted: 'rgba(0, 0, 0, 0.5)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  
  border: {
    primary: 'rgba(245, 124, 0, 0.6)',
    light: 'rgba(0, 0, 0, 0.12)',
    hover: 'rgba(245, 124, 0, 0.4)',
    focus: '#f57c00',
  },
  
  decorative: {
    orbPrimary: 'rgba(255, 152, 0, 0.06)',
    orbSecondary: 'rgba(255, 87, 34, 0.04)',
    shapePrimary: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 87, 34, 0.08))',
    shapeRing: 'rgba(255, 152, 0, 0.25)',
    glow: '0 0 30px rgba(255, 152, 0, 0.2)',
  },
  
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#388e3c',
    bg: 'rgba(46, 125, 50, 0.1)',
    border: 'rgba(46, 125, 50, 0.2)',
  },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
    bg: 'rgba(211, 47, 47, 0.1)',
    border: 'rgba(211, 47, 47, 0.2)',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
    bg: 'rgba(255, 152, 0, 0.1)',
    border: 'rgba(255, 152, 0, 0.2)',
  },
  info: {
    main: '#2196f3',
    light: '#64b5f6',
    dark: '#1976d2',
    bg: 'rgba(33, 150, 243, 0.1)',
    border: 'rgba(33, 150, 243, 0.2)',
  },

  logoAiText: { color: '#ffffff' },
  
  sidebarSection: {
    workoutSessions: { main: '#EF6C00', bg: alpha('#EF6C00', 0.50) },
    myCalendars: { main: '#7e57c2', bg: alpha('#7e57c2', 0.50) },
    workoutPlans: { main: '#43a047', bg: alpha('#43a047', 0.50) },
    workoutLogs: { main: '#EF6C00', bg: alpha('#EF6C00', 0.50) },
    locations: { main: '#00897b', bg: alpha('#00897b', 0.50) },
    activities: { main: '#e91e63', bg: alpha('#e91e63', 0.50) },
    equipment: { main: '#1976d2', bg: alpha('#1976d2', 0.50) },
  },
};

// ============================================================================
// STYLE GENERATOR FUNCTIONS
// ============================================================================

/**
 * Get colors based on mode
 */
export const getThemeColors = (mode) => mode === 'dark' ? darkColors : lightColors;

/**
 * Get input field styles
 */
export const getInputStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors.background.surface,
      borderRadius: '12px',
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: colors.border.light,
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: colors.border.hover,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.border.focus,
        borderWidth: '2px',
      },
      '&.Mui-focused': {
        backgroundColor: alpha(colors.primary.main, 0.05),
      },
    },
    '& .MuiInputLabel-root': {
      color: colors.text.muted,
      '&.Mui-focused': {
        color: colors.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      color: colors.text.primary,
    },
    '& .MuiFormHelperText-root': {
      color: colors.text.muted,
    },
  };
};

/**
 * Get primary button styles (gradient filled)
 */
export const getPrimaryButtonStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    py: 1.8,
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: '12px',
    background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
    textTransform: 'none',
    boxShadow: `0 4px 20px ${alpha(colors.primary.main, 0.3)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${colors.primary.light}, ${colors.secondary.light})`,
      boxShadow: `0 6px 28px ${alpha(colors.primary.main, 0.4)}`,
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: alpha(colors.primary.main, 0.3),
      color: alpha(colors.text.primary, 0.5),
    },
    transition: 'all 0.3s ease',
  };
};

/**
 * Get secondary button styles (outlined)
 */
export const getSecondaryButtonStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    py: 1.8,
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: '12px',
    borderColor: alpha(colors.primary.main, 0.5),
    borderWidth: '2px',
    color: colors.primary.main,
    textTransform: 'none',
    '&:hover': {
      borderColor: colors.primary.main,
      borderWidth: '2px',
      backgroundColor: alpha(colors.primary.main, 0.08),
    },
    '&:disabled': {
      borderColor: alpha(colors.primary.main, 0.2),
      color: alpha(colors.primary.main, 0.4),
    },
    transition: 'all 0.3s ease',
  };
};

/**
 * Get card/paper styles
 */
export const getCardStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    p: { xs: 3, md: 4 },
    borderRadius: '24px',
    background: colors.background.card,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.border.primary}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${alpha(colors.primary.main, 0.3)}, 0 0 20px ${alpha(colors.primary.main, 0.2)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, transparent, ${colors.primary.main}, transparent)`,
      opacity: 1,
    },
  };
};

/**
 * Get success alert styles
 */
export const getSuccessAlertStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    borderRadius: '12px',
    backgroundColor: colors.success.bg,
    border: `1px solid ${colors.success.border}`,
    '& .MuiAlert-icon': { color: colors.success.main },
  };
};

/**
 * Get error alert styles
 */
export const getErrorAlertStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    borderRadius: '12px',
    backgroundColor: colors.error.bg,
    border: `1px solid ${colors.error.border}`,
    '& .MuiAlert-icon': { color: colors.error.main },
  };
};

/**
 * Get link styles
 */
export const getLinkStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    color: colors.primary.main,
    fontWeight: 500,
    '&:hover': { color: colors.primary.light },
  };
};

/**
 * Get page background styles
 */
export const getPageBackgroundStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: colors.background.gradient,
    position: 'relative',
    overflow: 'hidden',
  };
};

/**
 * Get logo container styles
 */
export const getLogoContainerStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    position: 'relative',
    borderRadius: '16px',
    border: `5px solid ${colors.primary.main}`,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: colors.decorative.glow,
    flexShrink: 0,
    overflow: 'visible',
  };
};

/**
 * Get beta badge styles
 */
export const getBetaBadgeStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    position: 'absolute',
    top: -14,
    right: -16,
    px: 1.2,
    py: 0.5,
    background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
    borderRadius: '6px',
    border: '2px solid rgba(255, 255, 255, 0.25)',
    zIndex: 10,
    transform: 'rotate(15deg)',
    animation: `${appAnimations.betaPulse} 2s ease-in-out infinite`,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '4px',
      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%)',
    },
  };
};

/**
 * Get icon container styles (circular)
 */
export const getIconContainerStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: colors.decorative.shapePrimary,
    border: `2px solid ${colors.border.hover}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
};

/**
 * Get feature icon box styles
 */
export const getFeatureIconStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    width: 48,
    height: 48,
    borderRadius: '12px',
    background: colors.decorative.shapePrimary,
    border: `1px solid ${colors.decorative.shapeRing}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };
};

/**
 * Get footer styles
 */
export const getFooterStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    py: 4,
    px: 3,
    borderTop: `1px solid ${colors.border.light}`,
    position: 'relative',
    zIndex: 1,
  };
};

/**
 * Get underline styles
 */
export const getUnderlineStyles = (mode, width = '100%', height = '3px') => {
  const colors = getThemeColors(mode);
  return {
    height,
    width,
    background: `linear-gradient(90deg, transparent 0%, ${colors.primary.main} 30%, ${colors.primary.main} 70%, transparent 100%)`,
    borderRadius: '2px',
  };
};

/**
 * Get divider styles
 */
export const getDividerStyles = (mode) => {
  const colors = getThemeColors(mode);
  return {
    my: 3,
    '&::before, &::after': {
      borderColor: colors.border.light,
    },
  };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Split app name for styled display (e.g., "WorkoutSmart.AI" -> { main: "WorkoutSmart", suffix: ".AI" })
 */
export const splitAppName = (name) => {
  if (!name) return { main: '', suffix: '' };
  
  if (name.includes('.')) {
    const parts = name.split('.');
    return {
      main: parts[0],
      suffix: '.' + parts.slice(1).join('.'),
    };
  }
  
  const aiMatch = name.match(/^(.+?)(AI)$/i);
  if (aiMatch) {
    return {
      main: aiMatch[1],
      suffix: aiMatch[2],
    };
  }
  
  return {
    main: name,
    suffix: '',
  };
};

/**
 * Floating shape configurations
 */
export const floatingShapeConfigs = [
  { delay: 0, duration: 6, size: 60, top: '15%', left: '5%', shape: 'circle' },
  { delay: 1, duration: 8, size: 40, top: '70%', left: '8%', shape: 'square' },
  { delay: 2, duration: 7, size: 80, top: '25%', left: '75%', shape: 'ring' },
  { delay: 0.5, duration: 9, size: 50, top: '60%', left: '85%', shape: 'circle' },
  { delay: 1.5, duration: 6, size: 30, top: '80%', left: '50%', shape: 'square' },
];

export const floatingShapeConfigsAlt = [
  { delay: 0, duration: 6, size: 60, top: '15%', left: '5%', shape: 'circle' },
  { delay: 1, duration: 8, size: 40, top: '70%', left: '8%', shape: 'square' },
  { delay: 2, duration: 7, size: 80, top: '25%', left: '45%', shape: 'ring' },
  { delay: 0.5, duration: 9, size: 50, top: '60%', left: '85%', shape: 'circle' },
  { delay: 1.5, duration: 6, size: 30, top: '80%', left: '50%', shape: 'square' },
];

// ============================================================================
// DEFAULT THEME OPTIONS
// ============================================================================
const getDefaultThemeOptions = (mode, multiplier = 1) => {
  const baseFontSize = 15;
  const scaledFontSize = baseFontSize * multiplier;
  const colors = getThemeColors(mode);

  return {
    palette: {
      mode,
      primary: colors.primary,
      secondary: colors.secondary,
      background: {
        default: colors.background.default,
        paper: colors.background.paper,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
      },
      success: colors.success,
      error: colors.error,
      warning: colors.warning,
      info: colors.info,
      divider: colors.border.light,
      logoAiText: colors.logoAiText,
      sidebarSection: colors.sidebarSection,
      // Extended colors for custom usage
      border: colors.border,
      decorative: colors.decorative,
    },
    
    typography: {
      fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: scaledFontSize,
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },
    
    shape: {
      borderRadius: 12,
    },
    
    // Custom theme properties
    custom: {
      animations: appAnimations,
      colors: colors,
      floatingShapes: floatingShapeConfigs,
    },
    
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: mode === 'dark' ? '#4a4a4a #1a1a1a' : '#c1c1c1 #f1f1f1',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              width: 8,
              height: 8,
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              backgroundColor: mode === 'dark' ? '#4a4a4a' : '#c1c1c1',
              border: '2px solid transparent',
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: colors.primary.main,
            },
            '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
              backgroundColor: mode === 'dark' ? '#1a1a1a' : '#f1f1f1',
            },
          },
        },
      },
      
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 12,
            transition: 'all 0.3s ease',
          },
          contained: {
            boxShadow: `0 4px 14px ${alpha(colors.primary.main, 0.25)}`,
            '&:hover': {
              boxShadow: `0 6px 20px ${alpha(colors.primary.main, 0.35)}`,
              transform: 'translateY(-1px)',
            },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
            '&:hover': {
              background: `linear-gradient(135deg, ${colors.primary.light}, ${colors.secondary.light})`,
            },
          },
          outlined: {
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
              backgroundColor: alpha(colors.primary.main, 0.08),
            },
          },
        },
      },
      
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              transition: 'all 0.3s ease',
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary.main,
                  borderWidth: 2,
                },
              },
            },
          },
        },
      },
      
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: 16,
          },
          elevation1: {
            boxShadow: mode === 'dark'
              ? '0 2px 8px rgba(0, 0, 0, 0.4)'
              : '0 2px 8px rgba(0, 0, 0, 0.08)',
          },
          elevation3: {
            boxShadow: mode === 'dark'
              ? '0 4px 16px rgba(0, 0, 0, 0.5)'
              : '0 4px 16px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${colors.border.light}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: colors.border.hover,
              boxShadow: `0 8px 24px ${alpha(colors.primary.main, 0.15)}`,
            },
          },
        },
      },
      
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
          standardSuccess: {
            backgroundColor: colors.success.bg,
            border: `1px solid ${colors.success.border}`,
            '& .MuiAlert-icon': { color: colors.success.main },
          },
          standardError: {
            backgroundColor: colors.error.bg,
            border: `1px solid ${colors.error.border}`,
            '& .MuiAlert-icon': { color: colors.error.main },
          },
          standardWarning: {
            backgroundColor: colors.warning.bg,
            border: `1px solid ${colors.warning.border}`,
            '& .MuiAlert-icon': { color: colors.warning.main },
          },
          standardInfo: {
            backgroundColor: colors.info.bg,
            border: `1px solid ${colors.info.border}`,
            '& .MuiAlert-icon': { color: colors.info.main },
          },
        },
      },
      
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: colors.border.light,
          },
        },
      },
      
      MuiLink: {
        styleOverrides: {
          root: {
            color: colors.primary.main,
            fontWeight: 500,
            transition: 'color 0.2s ease',
            '&:hover': {
              color: colors.primary.light,
            },
          },
        },
      },
      
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(colors.primary.main, 0.1),
            },
          },
        },
      },
      
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#424242',
            borderRadius: 8,
            fontSize: '0.75rem',
            padding: '8px 12px',
          },
        },
      },
      
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            border: `1px solid ${colors.border.light}`,
          },
        },
      },
      
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${colors.border.light}`,
          },
        },
      },
      
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderBottom: `1px solid ${colors.border.light}`,
          },
        },
      },
      
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 8px',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(colors.primary.main, 0.1),
            },
            '&.Mui-selected': {
              backgroundColor: alpha(colors.primary.main, 0.15),
              '&:hover': {
                backgroundColor: alpha(colors.primary.main, 0.2),
              },
            },
          },
        },
      },
      
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
      
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            transition: 'all 0.2s ease',
          },
        },
      },
      
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: colors.primary.main,
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: colors.primary.main,
            },
          },
        },
      },
      
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: colors.primary.main,
            },
          },
        },
      },
      
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: colors.primary.main,
            },
          },
        },
      },
      
      MuiSlider: {
        styleOverrides: {
          root: {
            '& .MuiSlider-thumb': {
              '&:hover, &.Mui-focusVisible': {
                boxShadow: `0 0 0 8px ${alpha(colors.primary.main, 0.16)}`,
              },
            },
          },
        },
      },
      
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: alpha(colors.primary.main, 0.2),
          },
          bar: {
            borderRadius: 4,
          },
        },
      },
      
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: colors.primary.main,
          },
        },
      },
      
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(colors.text.primary, 0.08),
          },
        },
      },
      
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: colors.border.light,
          },
        },
      },
      
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${colors.border.light}`,
            boxShadow: mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.5)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 8px',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(colors.primary.main, 0.1),
            },
          },
        },
      },
      
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${colors.border.light}`,
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          },
        },
      },
      
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontWeight: 600,
          },
        },
      },
      
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: `0 4px 14px ${alpha(colors.primary.main, 0.3)}`,
            '&:hover': {
              boxShadow: `0 6px 20px ${alpha(colors.primary.main, 0.4)}`,
            },
          },
        },
      },
    },
  };
};

// ============================================================================
// MAIN THEME EXPORT
// ============================================================================

/**
 * Returns the appropriate MUI theme object based on mode and selected theme ID.
 */
export const getAppTheme = (mode, multiplier = 1, themeId = 'default') => {
  const baseFontSize = 15 * multiplier;
  let themeOptions;

  switch (themeId) {
    case 'premium':
      themeOptions = getPremiumTheme(mode, baseFontSize);
      break;
    case 'cyber':
      themeOptions = getCyberTheme(mode, baseFontSize);
      break;
    case 'default':
    default:
      themeOptions = getDefaultThemeOptions(mode, multiplier);
      break;
  }

  return createTheme(themeOptions);
};

// Export colors for direct access
export { darkColors, lightColors };

export default getAppTheme;